import { useLocalObservable } from '@euler/utils/hooks/useLocalObservable';
import { DependencyList } from 'react';
import { catchError, defer, exhaustMap, of, scan, timer } from 'rxjs';

const kRefreshMs = 60 * 1000;

export type Refreshing<T> =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; value: T; updatedAt: Date };

/**
 * What `load` gives, loaded again every minute. A reload that fails keeps
 * what the board shows (its 更新于 time says how old it is); only a first
 * load that fails shows the failure, and the next minute tries again.
 */
// eslint-disable-next-line @typescript-eslint/ban-types -- never null: null is a failed load
export function useRefreshing<T extends {}>(
  what: string,
  load: () => Promise<T>,
  deps: DependencyList,
): Refreshing<T> {
  return useLocalObservable<Refreshing<T>>(
    () =>
      timer(0, kRefreshMs).pipe(
        exhaustMap(() =>
          defer(load).pipe(
            catchError(error => {
              console.error(`workshop board: loading ${what} failed`, error);
              return of(null);
            }),
          ),
        ),
        scan<T | null, Refreshing<T>>(
          (state, value) =>
            value != null
              ? { kind: 'ready', value, updatedAt: new Date() }
              : state.kind === 'ready'
              ? state
              : { kind: 'failed' },
          { kind: 'loading' },
        ),
      ),
    { kind: 'loading' },
    deps,
  );
}
